import { RotateCcw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { IndexPage } from '../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../components/index-page/types';
import type { MenuItem } from '../../../components/Menu';
import { StatusText } from '../../../components/table/StatusText';
import { TextCell } from '../../../components/table/TextCell';
import { ApiError } from '../../../lib/apiError';
import { CashCatalogModal } from '../components/CashCatalogModal';
import { cashCatalogsApi } from '../cashCatalogsApi';
import type { CashCatalogDescriptor, CashCatalogEntry } from '../cashCatalogTypes';

/**
 * One of the Cash Division's two vocabularies — what a request is for, or how it is paid out.
 *
 * **One page for both**, parameterised by the descriptor, so a column added to one cannot be
 * forgotten on the other.
 */
export function CashCatalogIndexPage({ catalog }: { catalog: CashCatalogDescriptor }) {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<CashCatalogEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    entry: CashCatalogEntry;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchEntries = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      cashCatalogsApi.list(
        catalog.slug,
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [catalog.slug],
  );

  const rowActions = useCallback(
    (entry: CashCatalogEntry): MenuItem[] => [
      {
        key: `edit-${entry.id}`,
        label: 'Edit',
        permission: 'cash-catalogs.update',
        onClick: () => setEditing(entry),
      },
      {
        key: `restore-${entry.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'cash-catalogs.delete',
        disabledReason: entry.isActive ? `This ${catalog.noun} is already active.` : null,
        onClick: () => setConfirming({ kind: 'activate', entry }),
      },
    ],
    [catalog.noun],
  );

  const columns = useMemo(() => buildColumns(catalog), [catalog]);

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await cashCatalogsApi.remove(catalog.slug, confirming.entry.id);
      } else {
        await cashCatalogsApi.restore(catalog.slug, confirming.entry.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<CashCatalogEntry>
        // ONE module key for both vocabularies — maintaining the two is one administrative job.
        module="cash-catalogs"
        /*
          The shell derives its noun from the module key, which is wrong here: two vocabularies
          share the key, and "Clear all cash catalogs" would misstate the scope of the widest
          destructive action on the page. It clears THIS list.
        */
        moduleNoun={catalog.nounPlural}
        breadcrumb={['Cash Division', { label: 'Settings', to: '/cash-division/settings' }, catalog.label]}
        columns={columns}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={[catalog.entityType]}
        rowKey={(entry) => entry.id}
        fetch={fetchEntries}
        reloadKey={reloadKey}
        searchPlaceholder={`Search ${catalog.nounPlural}`}
        emptyMessage={catalog.emptyMessage}
        onCreate={() => setCreating(true)}
        createPermission="cash-catalogs.create"
        onRowOpen={(entry) => navigate(`${catalog.settingsPath}/${entry.id}`)}
        openPermission="cash-catalogs.read"
        rowActions={rowActions}
        onDelete={(entry) => setConfirming({ kind: 'deactivate', entry })}
        deletePermission="cash-catalogs.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(entry) => (entry.isActive ? null : `This ${catalog.noun} is already deleted.`)}
        onClearData={() => cashCatalogsApi.clear(catalog.slug)}
        clearDataPermission="cash-catalogs.clear-data"
        otherInfo={(entry) => entry}
      />

      {creating || editing !== null ? (
        <CashCatalogModal
          catalog={catalog}
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? `Delete ${catalog.noun}` : `Restore ${catalog.noun}`}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`cash-catalog-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.entry.name}" stops being offered on a new request.`
                  : `"${confirming.entry.name}" is offered again on a new request.`}
              </p>

              {/*
                THE COUNT BEFORE THE TICK, because the delete does less than its label suggests:
                a request already filed under this row keeps it, so a reader who expected those
                requests to change would otherwise see nothing happen.
              */}
              {confirming.kind === 'deactivate' && confirming.entry.requestCount > 0 ? (
                <p>
                  {confirming.entry.requestCount} request
                  {confirming.entry.requestCount === 1 ? ' is' : 's are'} already filed under it, and
                  {confirming.entry.requestCount === 1 ? ' keeps' : ' keep'} it — deleting a {catalog.noun}{' '}
                  does not change what a request says it was for.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted {catalog.noun} can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

function buildColumns(catalog: CashCatalogDescriptor): ColumnDef<CashCatalogEntry>[] {
  return [
    {
      key: 'name',
      header: catalog.columnHeader,
      width: 280,
      sortable: true,
      render: (entry) => <TextCell value={entry.name} />,
    },
    {
      key: 'description',
      header: 'Description',
      width: 420,
      flex: true,
      summary: true,
      render: (entry) => <TextCell value={entry.description} />,
    },
    {
      key: 'requestCount',
      header: 'Requests',
      width: 120,
      align: 'right',
      // Not a summary value — a bare count on the list line reads as nothing — and not
      // sortable: it is a correlated count the server's allow-list omits.
      render: (entry) => <TextCell value={String(entry.requestCount)} mono numeric />,
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      status: true,
      render: (entry) =>
        entry.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];
}
